import React, {useEffect} from 'react'

import classNames from 'classnames'
import {useMedia} from 'react-use'
import styled from 'styled-components'
import {media} from 'styled-bootstrap-grid'

import {MainButton} from '@festi/common/components/buttons'
import {useBambuser, useComparison} from '@festi/common/contexts'
import {gridTheme, styledTheme} from '@festi/common/themes'

import CompareDrawerCountBubble from './CompareDrawerCountBubble'

const Root = styled.div`
  position: fixed;
  bottom: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  justify-content: center;
  gap: 8px;
  z-index: ${styledTheme.zIndex.chatBubble};
  opacity: 0;
  pointer-events: none;
  transition: 100ms ease-in;

  &.show {
    opacity: 1;
    pointer-events: all;
  }

  &.scrollRowOpen {
    bottom: 88px;
  }

  &.bounce {
    animation: bounce 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  }

  ${media.sm`
    display: none;
  `}

  @keyframes bounce {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(5%);
    }
    100% {
      transform: translateY(0);
    }
  }
`

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-inline: 8px;
`

const Wrapper = styled.div`
  width: 100%;
  max-width: 180px;

  &.shadow {
    button {
      box-shadow: ${styledTheme.boxShadow.button};
    }
  }
`

interface Props {
  fullWidth?: boolean
  extraButton?: React.ReactNode
}

export default function StickyMobileButtons({fullWidth, extraButton}: Props) {
  const {
    isComparing,
    comparedProducts,
    compareDrawerBounce,
    compareDrawerExpanded,
    changeCompareDrawerPosition,
    setCompareDrawerExpanded,
  } = useComparison()
  const {setFilterRowOpen} = useBambuser()

  const showCompare = isComparing && !compareDrawerExpanded
  const show = showCompare || !!extraButton

  const isSmallMobile = useMedia(
    `(max-width: ${gridTheme.breakpoints.sm}px)`,
    false,
  )

  useEffect(() => {
    setFilterRowOpen(showCompare && !!extraButton && isSmallMobile)
  }, [showCompare, extraButton, isSmallMobile, setFilterRowOpen])

  return (
    <Root
      className={classNames({
        show,
        scrollRowOpen: changeCompareDrawerPosition,
        bounce: compareDrawerBounce,
      })}
    >
      {showCompare && (
        <Wrapper>
          <MainButton
            label={
              <LabelWrapper>
                <span>Samanburður</span>{' '}
                <CompareDrawerCountBubble
                  count={comparedProducts?.length || 0}
                />
              </LabelWrapper>
            }
            buttonVariant="secondary"
            aria-label="Open compare drawer"
            fullWidth={fullWidth}
            onClick={() => setCompareDrawerExpanded(true)}
          />
        </Wrapper>
      )}

      {extraButton && <Wrapper className="shadow">{extraButton}</Wrapper>}
    </Root>
  )
}
